import { useEffect, useState, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Grid,
  Select,
} from '@chakra-ui/react'
import { SearchableSelect } from '@/components/common/SearchableSelect'
import { 
  createBudgetSchema, updateBudgetSchema, type CreateBudgetFormData, type UpdateBudgetFormData,
  editIssuedBudgetSchema, editTruckTripExpenseSchema, editPartsExpenseSchema, editFundsForStackRunSchema, editDriverCashAdvanceSchema, editHelperCashAdvanceSchema,
  type EditIssuedBudgetFormData, type EditTruckTripExpenseFormData, type EditPartsExpenseFormData, type EditFundsForStackRunFormData, type EditDriverCashAdvanceFormData, type EditHelperCashAdvanceFormData
  } from '@/lib/validations/auth' // renamed type for clarity
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useFieldArray } from 'react-hook-form'

type PersonDropdownOption = { id: number; full_name: string }
type TruckDropdownOption = { id: number; plate_number: string }

function mapDriverRow(d: Record<string, unknown>): PersonDropdownOption {
  const fromParts = [d.first_name, d.last_name].filter(Boolean).join(' ').trim()
  return {
    id: d.id as number,
    full_name: String(d.full_name ?? (fromParts || 'Unknown')),
  }
}

function mapHelperRow(h: Record<string, unknown>): PersonDropdownOption {
  return mapDriverRow(h)
}

interface BudgetManagamentFormProps {
  budgetManagementId?: number
  budgetManagementExpenseType?: string
  onSuccess: () => void
  onCancel: () => void
}

export function BudgetManagementForm({ budgetManagementId, budgetManagementExpenseType, onSuccess, onCancel }: BudgetManagamentFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!budgetManagementId
  const { showError, showSuccess } = useToastNotification()

  type dayNightShift = 'Day' | 'Night'
  
  const [dayNightShiftOptions, setdayNightShiftOptions] = useState<dayNightShift[]>(['Day', 'Night'])
  const [drivers, setDrivers] = useState<PersonDropdownOption[]>([])
  const [helpers, setHelpers] = useState<PersonDropdownOption[]>([])
  const [trucks, setTrucks] = useState<TruckDropdownOption[]>([])
  const [isLoadingDropdownData, setIsLoadingDropdownData] = useState(true)

      
      // Map display names to schema literal values
      const getExpenseTypeValue = (displayName?: string): string => {
        switch (displayName) {
          case "Issued Budget":
            return "issuedBudget"
          case "Truck Expense":
            return "truckTripExpense"
          case "Parts Expense":
            return "partsExpense"
          case "Other Expense":
            return "fundsForStackRun"
          case "Driver Cash Advance":
            return "driverCashAdvance"
          case "Helper Cash Advance":
            return "helperCashAdvance"
          default:
            return "issuedBudget"
        }
      }
    
      // Get the appropriate schema based on expense type (memoized to prevent re-renders)
      const schema = useMemo(() => {
        const selectedSchema = (() => {
          switch (budgetManagementExpenseType) {
            case "Issued Budget":
              return editIssuedBudgetSchema
            case "Truck Expense":
              return editTruckTripExpenseSchema
            case "Parts Expense":
              return editPartsExpenseSchema
            case "Other Expense":
              return editFundsForStackRunSchema
            case "Driver Cash Advance":
              return editDriverCashAdvanceSchema
            case "Helper Cash Advance":
              return editHelperCashAdvanceSchema
            default:
              return editIssuedBudgetSchema
          }
        })()
        console.log('[BudgetForm] Using schema for expense type:', budgetManagementExpenseType, selectedSchema)
        return selectedSchema
      }, [budgetManagementExpenseType])

  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
  } = useForm<CreateBudgetFormData | EditTruckTripExpenseFormData | EditPartsExpenseFormData | EditFundsForStackRunFormData | EditDriverCashAdvanceFormData | EditHelperCashAdvanceFormData>({
    resolver: zodResolver(isEditMode ? schema : createBudgetSchema),
    defaultValues: {
      expenseItems: [],
    },
  })
  
  const { fields, append, remove } = useFieldArray({
    control,
    name: "expenseItems",
    })
  const selectedType = watch("expenseType")

  useEffect(() => {
    if (!isEditMode) {
      reset({
        expenseType: selectedType as any,
        expenseItems: [],
      })
    }
  }, [selectedType, isEditMode, reset])

  useEffect(() => {
    let cancelled = false
    const activeListParams = { is_active: 1 as const }

    const load = async () => {
      setIsLoadingDropdownData(true)
      try {
        let issuedPayload: Record<string, unknown> | null = null
        let truckTripPayload: Record<string, unknown> | null = null
        let partsPayload: Record<string, unknown> | null = null
        let otherPayload: Record<string, unknown> | null = null
        let driverCAPayload: Record<string, unknown> | null = null
        let helperCAPayload: Record<string, unknown> | null = null

        if (isEditMode && budgetManagementId && budgetManagementExpenseType) {
          switch (budgetManagementExpenseType) {
            case 'Issued Budget': {
              const { data } = await apiClient.get(`/budget/issued-budget/${budgetManagementId}`)
              issuedPayload = data?.data || data || {}
              break
            }
            case 'Truck Expense': {
              const { data } = await apiClient.get(`/budget/truck-trip-expense/${budgetManagementId}`)
              truckTripPayload = data?.data || data || {}
              break
            }
            case 'Parts Expense': {
              const { data } = await apiClient.get(`/budget/parts-expense/${budgetManagementId}`)
              partsPayload = data?.data || data || {}
              break
            }
            case 'Other Expense': {
              const { data } = await apiClient.get(`/budget/funds-for-stack-run/${budgetManagementId}`)
              otherPayload = data?.data || data || {}
              break
            }
            case 'Driver Cash Advance': {
              const { data } = await apiClient.get(`/cash-advances/${budgetManagementId}?type=1`)
              driverCAPayload = data?.data || data || {}
              break
            }
            case 'Helper Cash Advance': {
              const { data } = await apiClient.get(`/cash-advances/${budgetManagementId}?type=2`)
              helperCAPayload = data?.data || data || {}
              break
            }
            default:
              break
          }
        }

        if (cancelled) return

        const [driversRes, helpersRes, trucksRes] = await Promise.all([
          apiClient.get('/drivers', { params: activeListParams }),
          apiClient.get('/helpers', { params: activeListParams }),
          apiClient.get('/trucks/truck-list', { params: activeListParams }),
        ])

        if (cancelled) return

        const driversRaw = (driversRes.data?.data || driversRes.data || []) as Record<string, unknown>[]
        let driversList = driversRaw.map(mapDriverRow)

        const helpersRaw = (helpersRes.data?.data || helpersRes.data || []) as Record<string, unknown>[]
        let helpersList = helpersRaw.map(mapHelperRow)

        let trucksList = ((trucksRes.data?.data || trucksRes.data || []) as Record<string, unknown>[]).map(
          (t) => ({
            id: t.id as number,
            plate_number: String(t.plate_number ?? ''),
          })
        )

        if (truckTripPayload) {
          const hid = truckTripPayload.helper_id as number | null | undefined
          const hname = truckTripPayload.helper_name as string | undefined
          if (hid != null && !helpersList.some((h) => h.id === hid)) {
            helpersList = [{ id: hid, full_name: hname || 'Unknown' }, ...helpersList]
          }
          const plate = truckTripPayload.plate_number as string | null | undefined
          if (plate && !trucksList.some((t) => t.plate_number === plate)) {
            trucksList = [{ id: plate as unknown as number, plate_number: plate }, ...trucksList]
          }
        }

        if (partsPayload?.plate_number) {
          const plate = String(partsPayload.plate_number)
          if (!trucksList.some((t) => t.plate_number === plate)) {
            trucksList = [{ id: plate as unknown as number, plate_number: plate }, ...trucksList]
          }
        }

        if (driverCAPayload?.driver_id != null) {
          const did = driverCAPayload.driver_id as number
          const dname = (driverCAPayload.driver_name as string) || 'Unknown'
          if (!driversList.some((d) => d.id === did)) {
            driversList = [{ id: did, full_name: dname }, ...driversList]
          }
        }

        if (helperCAPayload?.helper_id != null) {
          const hid = helperCAPayload.helper_id as number
          const hname = (helperCAPayload.helper_name as string) || 'Unknown'
          if (!helpersList.some((h) => h.id === hid)) {
            helpersList = [{ id: hid, full_name: hname }, ...helpersList]
          }
        }

        setDrivers(driversList)
        setHelpers(helpersList)
        setTrucks(trucksList)

        if (cancelled) return

        if (isEditMode && budgetManagementExpenseType) {
          if (issuedPayload) {
            const b = issuedPayload
            reset({
              shift: b.shift,
              dateIssued: b.transaction_date,
              source: b.source,
              amount: b.amount,
            } as any)
          } else if (truckTripPayload) {
            const b = truckTripPayload
            reset({
              shift: b.shift,
              plateNumber: b.plate_number ?? '',
              helperID: b.helper_id,
              cashOnHand: b.cash_on_hand,
              issuedCashAmount: b.issued_cash_amount,
              transactionDate: b.transaction_date,
            } as any)
          } else if (partsPayload) {
            const b = partsPayload
            reset({
              shift: b.shift,
              plateNumber: b.plate_number,
              receiptNO: b.receipt_no,
              quantity: b.quantity,
              article: b.article,
              amountPerItem: b.amount_per_item,
              transactionDate: b.transaction_date,
            } as any)
          } else if (otherPayload) {
            const b = otherPayload
            reset({
              shift: b.shift,
              remarks: b.remarks,
              amount: b.amount,
              transactionDate: b.transaction_date,
            } as any)
          } else if (driverCAPayload) {
            const b = driverCAPayload
            reset({
              shift: b.shift,
              driverID: b.driver_id,
              amount: b.amount,
              transactionDate: b.transaction_date,
            } as any)
          } else if (helperCAPayload) {
            const b = helperCAPayload
            reset({
              shift: b.shift,
              helperID: b.helper_id,
              amount: b.amount,
              transactionDate: b.transaction_date,
            } as any)
          }
        }
      } catch (error: unknown) {
        showError(error)
      } finally {
        if (!cancelled) setIsLoadingDropdownData(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [isEditMode, budgetManagementExpenseType, budgetManagementId, reset, showError])

  const onSubmit = async (data: any) => {
    setIsSubmitting(true)

    const formattedData = {
        ...data,
        expenseType: getExpenseTypeValue(budgetManagementExpenseType)
      }
    try
    {
      if (isEditMode) {
        // expenseTypeKey is no longer needed but kept for reference if later required

        switch (budgetManagementExpenseType) {
        case "Issued Budget":
          const editIssuedBudgetData = formattedData as EditIssuedBudgetFormData
          await apiClient.patch(`/budget/issued-budget/${budgetManagementId}`, {
            shift: editIssuedBudgetData.shift,
            transaction_date: editIssuedBudgetData.dateIssued,
            source: editIssuedBudgetData.source,
            amount: editIssuedBudgetData.amount,
          })
          showSuccess('Success', 'Issued Budget successfully updated')
          break

        case "Truck Expense":
          const editTruckTripExpenseData = formattedData as EditTruckTripExpenseFormData
          await apiClient.patch(`/budget/truck-trip-expense/${budgetManagementId}`, {
            shift: editTruckTripExpenseData.shift,
            plate_number: editTruckTripExpenseData.plateNumber.trim(),
            helper_id: editTruckTripExpenseData.helperID,
            cash_on_hand: editTruckTripExpenseData.cashOnHand,
            issued_cash_amount: editTruckTripExpenseData.issuedCashAmount,
            transaction_date: editTruckTripExpenseData.transactionDate,
          })
          showSuccess('Success', 'Truck Trip Expense successfully updated')
          break

        case "Parts Expense":
          const editPartsExpenseData = formattedData as EditPartsExpenseFormData
          await apiClient.patch(`/budget/parts-expense/${budgetManagementId}`, {
            shift: editPartsExpenseData.shift,
            plate_number: editPartsExpenseData.plateNumber,
            receipt_no: editPartsExpenseData.receiptNO,
            quantity: editPartsExpenseData.quantity,
            article: editPartsExpenseData.article,
            amount_per_item: editPartsExpenseData.amountPerItem,
            transaction_date: editPartsExpenseData.transactionDate,
          })
          showSuccess('Success', 'Parts Expense successfully updated')
          break

        case "Other Expense":
          const editFundsForStackRunData = formattedData as EditFundsForStackRunFormData
          await apiClient.patch(`/budget/funds-for-stack-run/${budgetManagementId}`, {
            shift: editFundsForStackRunData.shift,
            remarks: editFundsForStackRunData.remarks,
            amount: editFundsForStackRunData.amount,
            transaction_date: editFundsForStackRunData.transactionDate,
          })
          showSuccess('Success', 'Other Expense successfully updated')
          break

        case "Driver Cash Advance":
          const editDriverCashAdvanceData = formattedData as EditDriverCashAdvanceFormData
          await apiClient.patch(`/cash-advances/${budgetManagementId}`, {
            type: 1,
            requestor_id: editDriverCashAdvanceData.driverID,
            amount: editDriverCashAdvanceData.amount,
            transaction_date: editDriverCashAdvanceData.transactionDate,
            shift: editDriverCashAdvanceData.shift,
          })
          showSuccess('Success', 'Driver Cash Advance successfully updated')
          break

        case "Helper Cash Advance":
          const editHelperCashAdvanceData = formattedData as EditHelperCashAdvanceFormData
          await apiClient.patch(`/cash-advances/${budgetManagementId}`, {
            type: 2,
            requestor_id: editHelperCashAdvanceData.helperID,
            amount: editHelperCashAdvanceData.amount,
            transaction_date: editHelperCashAdvanceData.transactionDate,
            shift: editHelperCashAdvanceData.shift,
          })
          showSuccess('Success', 'Helper Cash Advance successfully updated')
          break

        default:
          throw new Error('Unknown expense type')
      }
      }
      else { // Not in edit mode - create new record
        switch (data.expenseType) {
        case "issuedBudget":
          const issuedBudgetData = data as Extract<CreateBudgetFormData, { expenseType: "issuedBudget" }>
          await apiClient.post("/budget/issued-budget", {
            shift: issuedBudgetData.shift,
            transaction_date: issuedBudgetData.dateIssued,
            source: issuedBudgetData.source,
            amount: issuedBudgetData.amount,
            })
          showSuccess('Success', 'Issued Budget successfully created')
          break

        case "truckTripExpense":
          const truckTripData = data as Extract<CreateBudgetFormData, { expenseType: "truckTripExpense" }>

          await Promise.all(
            truckTripData.expenseItems.map((item) => apiClient.post('/budget/truck-trip-expense', {
              shift: item.shift,
              plate_number: item.plateNumber.trim(),
              helper_id: item.helperID,
              cash_on_hand: item.cashOnHand,
              issued_cash_amount: item.issuedCashAmount,
              transaction_date: item.transactionDate,
              })
            )
          );
          showSuccess('Success', 'Truck Trip Expense successfully created')
          break

        case "partsExpense":
          const partsExpenseData = data as Extract<CreateBudgetFormData, { expenseType: "partsExpense" }>
          await Promise.all(
            partsExpenseData.expenseItems.map((item) => apiClient.post('/budget/parts-expense', {
              shift: item.shift,
              plate_number: item.plateNumber,
              receipt_no: item.receiptNO,
              quantity: item.quantity,
              article: item.article,
              amount_per_item: item.amountPerItem,
              transaction_date: item.transactionDate,
              })
            )
          );
          showSuccess('Success', 'Parts Expense successfully created')
          break

        case "fundsForStackRun":
          const fundsForStackRunData = data as Extract<CreateBudgetFormData, { expenseType: "fundsForStackRun" }>
          await Promise.all(
            fundsForStackRunData.expenseItems.map((item) => apiClient.post('/budget/funds-for-stack-run', {
              shift: item.shift,
              remarks: item.remarks,
              amount: item.amount,
              transaction_date: item.transactionDate,
              })
            )
          );
          showSuccess('Success', 'Other Expense successfully created')
          break

        case "driverCashAdvance":
          const driverCashAdvanceData = data as Extract<CreateBudgetFormData, { expenseType: "driverCashAdvance" }>
          await Promise.all(
            driverCashAdvanceData.expenseItems.map((item) => apiClient.post('/cash-advances', {
              type: 1,
              requestor_id: item.driverID,
              amount: item.amount,
              transaction_date: item.transactionDate,
              shift: item.shift,
              })
            )
          );
          showSuccess('Success', 'Driver Cash Advance successfully created')
          break

        case "helperCashAdvance":
          const helperCashAdvanceData = data as Extract<CreateBudgetFormData, { expenseType: "helperCashAdvance" }>
          await Promise.all(
            helperCashAdvanceData.expenseItems.map((item) => apiClient.post('/cash-advances', {
              type: 2,
              requestor_id: item.helperID,
              amount: item.amount,
              transaction_date: item.transactionDate,
              shift: item.shift,
              })
            )
          );
          showSuccess('Success', 'Helper Cash Advance successfully created')
          break
      }
    }

      onSuccess?.()

    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      
      <VStack spacing={4} align="stretch">
        {isEditMode === false && (
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Expense Type <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
            <Select
              placeholder="Select expense type"
              {...register("expenseType")}
            >
              <option value="issuedBudget">Issued Budget</option>
              <option value="truckTripExpense">Truck Trip Expense</option>
              <option value="partsExpense">Parts Expense</option>
              <option value="fundsForStackRun">Funds for Stack Run</option>
              <option value="driverCashAdvance">Driver Cash Advance</option>
              <option value="helperCashAdvance">Helper Cash Advance</option>
            </Select>
        </Grid>
        )}

        
        {/* Add Income Section */}
        {isEditMode === false && selectedType === 'issuedBudget' && (
          <VStack align="stretch" spacing={3}>
            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Time of Shift <Text as="span" color="red.500">*</Text>
              </FormLabel>
              <Select placeholder="Time of Shift"
                  {...register('shift')}
                  >
                  {dayNightShiftOptions.map((shift) => ( <option key={shift} value={shift}> {shift} </option> ))}
                </Select>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Date Issued <Text as="span" color="red.500">
                  *
                </Text>
              </FormLabel>
              <Input type="date" {...register('dateIssued')} placeholder="Input the Date Issued"/>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Source <Text as="span" color="red.500">
                *
              </Text>
            </FormLabel>
            <Input type="text" {...register('source')} placeholder="Input the Source"/>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Amount <Text as="span" color="red.500">
                *
              </Text>
            </FormLabel>
            <Input type="number" {...register('amount', { valueAsNumber: true })} placeholder="Input the Amount"/>
          </Grid>
          
          </VStack>
        )}

        {/* Truck Trip Expense Section */}
        {isEditMode === false && selectedType === "truckTripExpense" && (
          <VStack align="stretch" spacing={3}>
            <Grid templateColumns="1fr 1.25fr 2fr 1fr 1fr 1fr 0.5fr auto" gap={3}>
              <Text fontWeight="bold">
                Shift{' '}
                <Text as="span" color="red.500">
                  *
                </Text>
              </Text>
              <Text fontWeight="bold">
                Plate Number{' '}
                <Text as="span" color="red.500">
                  *
                </Text>
              </Text>
              <Text fontWeight="bold">
                Helper{' '}
                <Text as="span" color="red.500">
                  *
                </Text>
              </Text>
              <Text fontWeight="bold">
                Cash on Hand{' '}
                <Text as="span" color="red.500">
                  *
                </Text>
              </Text>
              <Text fontWeight="bold">
                Issued Cash{' '}
                <Text as="span" color="red.500">
                  *
                </Text>
              </Text>
              <Text fontWeight="bold">
                Date{' '}
                <Text as="span" color="red.500">
                  *
                </Text>
              </Text>
            </Grid>

            {fields.map((field, index) => (
              <Grid
                key={field.id}
                templateColumns="1fr 1.25fr 2fr 1fr 1fr 1fr 0.5fr auto"
                gap={3}
                alignItems="center"
                >
                <Select {...register(`expenseItems.${index}.shift`)} placeholder="Select shift">
                  <option value="Day">Day</option>
                  <option value="Night">Night</option>
                </Select>

                <Controller
                  name={`expenseItems.${index}.plateNumber`}
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={trucks.map((truck) => ({
                        id: truck.plate_number,
                        name: truck.plate_number,
                      }))}
                      optionsUrl="/trucks/truck-list"
                      optionsParams={{ is_active: 1 }}
                      optionIdKey="plate_number"
                      value={field.value ? field.value : null}
                      onChange={(v) => field.onChange(v != null ? String(v) : '')}
                      placeholder="Select plate number"
                      isDisabled={isLoadingDropdownData}
                    />
                  )}
                />

                <Controller
                  name={`expenseItems.${index}.helperID`}
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={helpers.map((h) => ({ id: h.id, name: h.full_name }))}
                      optionsUrl="/helpers"
                      optionsParams={{ is_active: 1 }}
                      value={field.value && Number(field.value) > 0 ? field.value : null}
                      onChange={(v) => field.onChange(v != null ? Number(v) : 0)}
                      placeholder="Select helper"
                      isDisabled={isLoadingDropdownData}
                    />
                  )}
                />

                <Input
                  type="number"
                  {...register(`expenseItems.${index}.cashOnHand`, { valueAsNumber: true })}
                  />

                <Input
                  type="number"
                  {...register(`expenseItems.${index}.issuedCashAmount`, { valueAsNumber: true })}
                  />

                <Input
                  type="date"
                  {...register(`expenseItems.${index}.transactionDate`)}
                  />

                <Button size="sm" onClick={() => remove(index)}>
                  Remove
                </Button>
              </Grid>
            ))}

            <Button
              size="sm"
              onClick={() =>
                append({
                  shift: "",
                  plateNumber: "",
                  helperID: 0,
                  cashOnHand: 0,
                  issuedCashAmount: 0,
                  transactionDate: "",
                })
              }
            >
              + Add Row
            </Button>
          </VStack>
        )}


        {/* Parts Expense Section */}
        {isEditMode === false && selectedType === "partsExpense" && (
          <VStack align="stretch" spacing={3}>
            <Grid templateColumns="1.1fr 1.25fr 1fr 0.75fr 1.75fr 1.25fr 1.25fr 0.5fr auto" gap={3}>
              <Text fontWeight="bold">Time of Shift</Text>
              <Text fontWeight="bold">Plate Number</Text>
              <Text fontWeight="bold">Receipt NO</Text>
              <Text fontWeight="bold">Quantity</Text>
              <Text fontWeight="bold">Article</Text>
              <Text fontWeight="bold">Amount</Text>
              <Text fontWeight="bold">Transaction Date</Text>
            </Grid>

            {fields.map((field, index) => (
              <Grid
                key={field.id}
                templateColumns="1.1fr 1.25fr 1fr 0.75fr 1.75fr 1.25fr 1.25fr 0.5fr auto" 
                gap={3}
                alignItems="center"
                >
                  <Select {...register(`expenseItems.${index}.shift`)}>
                    <option value="Day">Day</option>
                    <option value="Night">Night</option>
                  </Select>

                  <Controller
                    name={`expenseItems.${index}.plateNumber`}
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        options={trucks.map((truck) => ({
                          id: truck.plate_number,
                          name: truck.plate_number,
                        }))}
                        optionsUrl="/trucks/truck-list"
                        optionsParams={{ is_active: 1 }}
                        optionIdKey="plate_number"
                        value={field.value ? field.value : null}
                        onChange={(v) => field.onChange(v != null ? String(v) : '')}
                        placeholder="Select plate"
                        isDisabled={isLoadingDropdownData}
                      />
                    )}
                  />

                  <Input
                    type="text"
                    {...register(`expenseItems.${index}.receiptNO`)}
                    />

                  <Input
                    type="number"
                    {...register(`expenseItems.${index}.quantity`, { valueAsNumber: true })}
                    />

                  <Input
                    type="text"
                    {...register(`expenseItems.${index}.article`)}
                  />

                  <Input
                    type="number"
                    {...register(`expenseItems.${index}.amountPerItem`, { valueAsNumber: true })}
                    />

                  <Input
                    type="date"
                    {...register(`expenseItems.${index}.transactionDate`)}
                    />

                  <Button size="sm" onClick={() => remove(index)}>
                    Remove
                  </Button>
              </Grid>
            ))}

            <Button
              size="sm"
              onClick={() =>
                append({
                  shift: "",
                  plateNumber: "",
                  receiptNO: "",
                  quantity: 0,
                  article: "",
                  amountPerItem: 0,
                  transactionDate: "",
                })
              }
            >
              + Add Row
            </Button>
          </VStack>
        )}


        {/* Funds For Stack Run Section */}
        {isEditMode === false && selectedType === "fundsForStackRun" && (
          <VStack align="stretch" spacing={3}>
            <Grid templateColumns="1.1fr 3fr 1fr 1fr 0.5fr auto" gap={3}>
              <Text fontWeight="bold">Time of Shift</Text>
              <Text fontWeight="bold">Remark</Text>
              <Text fontWeight="bold">Amount</Text>
              <Text fontWeight="bold">Date Issued</Text>
            </Grid>

            {fields.map((field, index) => (
              <Grid
                key={field.id}
                templateColumns="1.1fr 3fr 1fr 1fr 0.5fr auto" gap={3}
                alignItems="center"
                >
                <Select {...register(`expenseItems.${index}.shift`)}>
                  <option value="Day">Day</option>
                  <option value="Night">Night</option>
                </Select>

                <Input
                  type="text"
                  {...register(`expenseItems.${index}.remarks`)}
                  />

                <Input
                  type="number"
                  {...register(`expenseItems.${index}.amount`, { valueAsNumber: true })}
                  />

                <Input
                  type="date"
                  {...register(`expenseItems.${index}.transactionDate`)}
                  />

                <Button size="sm" onClick={() => remove(index)}>
                  Remove
                </Button>
              </Grid>
            ))}

            <Button
              size="sm"
              onClick={() =>
                append({
                  shift: "",
                  remarks: "",
                  amount: 0,
                  transactionDate: "",
                })
              }
              >
              + Add Row
            </Button>
          </VStack>
        )}


        {/* Driver Cash Expense Section */}
        {isEditMode === false && selectedType === "driverCashAdvance" && (
        <VStack align="stretch" spacing={3}>
          <Grid templateColumns="0.75fr 1.1fr 1fr 2fr 0.5fr auto" gap={3}>
            <Text fontWeight="bold">Amount</Text>
            <Text fontWeight="bold">Transaction Date</Text>
            <Text fontWeight="bold">Time of Shift</Text>
            <Text fontWeight="bold">Driver Name</Text>
          </Grid>

          {fields.map((field, index) => (
            <Grid
              key={field.id}
              templateColumns="0.75fr 1.1fr 1fr 2fr 0.5fr auto" gap={3}
              alignItems="center"
              >
                <Input
                  type="number"
                  {...register(`expenseItems.${index}.amount`, { valueAsNumber: true })}
                  />

                <Input
                  type="date"
                  {...register(`expenseItems.${index}.transactionDate`)}
                  />

                <Select {...register(`expenseItems.${index}.shift`)}>
                  <option value="Day">Day</option>
                  <option value="Night">Night</option>
                </Select>

                <Controller
                  name={`expenseItems.${index}.driverID`}
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={drivers.map((d) => ({ id: d.id, name: d.full_name }))}
                      optionsUrl="/drivers"
                      optionsParams={{ is_active: 1 }}
                      value={field.value && Number(field.value) > 0 ? field.value : null}
                      onChange={(v) => field.onChange(v != null ? Number(v) : 0)}
                      placeholder="Select driver"
                      isDisabled={isLoadingDropdownData}
                    />
                  )}
                />

                <Button size="sm" onClick={() => remove(index)}>
                  Remove
                </Button>
              </Grid>
            ))}

            <Button
              size="sm"
              onClick={() =>
                append({
                  amount: 0,
                  transactionDate: "",
                  shift: "",
                  driverID: 0,
                  })
                }
              >
              + Add Row
            </Button>
          </VStack>
        )}


        {/* Helper Cash Expense Section */}
        {isEditMode === false && selectedType === "helperCashAdvance" && (
        <VStack align="stretch" spacing={3}>
          <Grid templateColumns="0.75fr 1.1fr 1fr 2fr 0.5fr auto" gap={3}>
            <Text fontWeight="bold">Amount</Text>
            <Text fontWeight="bold">Transaction Date</Text>
            <Text fontWeight="bold">Time of Shift</Text>
            <Text fontWeight="bold">Helper Name</Text>
          </Grid>

          {fields.map((field, index) => (
            <Grid
              key={field.id}
              templateColumns="0.75fr 1.1fr 1fr 2fr 0.5fr auto" gap={3}
              alignItems="center"
              >
                <Input
                  type="number"
                  {...register(`expenseItems.${index}.amount`, { valueAsNumber: true })}
                  />

                <Input
                  type="date"
                  {...register(`expenseItems.${index}.transactionDate`)}
                  />

                <Select {...register(`expenseItems.${index}.shift`)}>
                  <option value="Day">Day</option>
                  <option value="Night">Night</option>
                </Select>

                <Controller
                  name={`expenseItems.${index}.helperID`}
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={helpers.map((h) => ({ id: h.id, name: h.full_name }))}
                      optionsUrl="/helpers"
                      optionsParams={{ is_active: 1 }}
                      value={field.value && Number(field.value) > 0 ? field.value : null}
                      onChange={(v) => field.onChange(v != null ? Number(v) : 0)}
                      placeholder="Select helper"
                      isDisabled={isLoadingDropdownData}
                    />
                  )}
                />

                <Button size="sm" onClick={() => remove(index)}>
                  Remove
                </Button>
              </Grid>
            ))}

            <Button
              size="sm"
              onClick={() =>
                append({
                  amount: 0,
                  transactionDate: "",
                  shift: "",
                  helperID: 0,
                  })
                }
              >
              + Add Row
            </Button>
          </VStack>
        )}

        
        {/* Show Validation Errors */}
                {Object.keys(errors).length > 0 && (
                  <Box bg="red.50" p={3} borderRadius="md" borderLeft="4px" borderColor="red.500">
                    <Text color="red.700" fontSize="sm" fontWeight="bold">Form Validation Errors:</Text>
                    {Object.entries(errors).map(([field, error]: [string, any]) => (
                      <Text key={field} color="red.600" fontSize="sm">
                        {field}: {error?.message || 'Invalid'}
                      </Text>
                    ))}
                  </Box>
                )}
                
                {/* Add Income Section */}
                {isEditMode === true && budgetManagementExpenseType === 'Issued Budget' && (
                  <VStack align="stretch" spacing={3}>
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                      <FormLabel pt={2} fontWeight="medium">
                        Time of Shift <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Select placeholder="Time of Shift"
                        {...register('shift')}
                        >
                          {dayNightShiftOptions.map((shift) => ( <option key={shift} value={shift}> {shift} </option> ))}
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                      <FormLabel pt={2} fontWeight="medium">
                        Date Issued <Text as="span" color="red.500">
                          *
                        </Text>
                      </FormLabel>
                      <Input type="date" {...register('dateIssued')} placeholder="Input the Date Issued"/>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                    <FormLabel pt={2} fontWeight="medium">
                      Source <Text as="span" color="red.500">
                        *
                      </Text>
                    </FormLabel>
                    <Input type="text" {...register('source')} placeholder="Input the Source"/>
                  </Grid>
        
                  <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                    <FormLabel pt={2} fontWeight="medium">
                      Amount <Text as="span" color="red.500">
                        *
                      </Text>
                    </FormLabel>
                    <Input type="number" {...register('amount', { valueAsNumber: true })} placeholder="Input the Amount"/>
                  </Grid>
                  
                  </VStack>
                )}
        
                {isEditMode === true && budgetManagementExpenseType === "Truck Expense" && (
                  <VStack align="stretch" spacing={3}>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>
                        Shift{' '}
                        <Text as="span" color="red.500">
                          *
                        </Text>
                      </FormLabel>
                      <Select {...register('shift')} placeholder="Select shift">
                        <option value="Day">Day</option>
                        <option value="Night">Night</option>
                      </Select>
                    </Grid>

                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>
                        Plate Number{' '}
                        <Text as="span" color="red.500">
                          *
                        </Text>
                      </FormLabel>
                      <Controller
                        name="plateNumber"
                        control={control}
                        render={({ field }) => (
                          <SearchableSelect
                            options={trucks.map((truck) => ({
                              id: truck.plate_number,
                              name: truck.plate_number,
                            }))}
                            optionsUrl="/trucks/truck-list"
                            optionsParams={{ is_active: 1 }}
                            optionIdKey="plate_number"
                            value={field.value ? field.value : null}
                            onChange={(v) => field.onChange(v != null ? String(v) : '')}
                            placeholder="Select plate"
                            isDisabled={isLoadingDropdownData}
                          />
                        )}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>
                        Helper{' '}
                        <Text as="span" color="red.500">
                          *
                        </Text>
                      </FormLabel>
                      <Controller
                        name="helperID"
                        control={control}
                        render={({ field }) => (
                          <SearchableSelect
                            options={helpers.map((h) => ({ id: h.id, name: h.full_name }))}
                            optionsUrl="/helpers"
                            optionsParams={{ is_active: 1 }}
                            value={field.value != null && Number(field.value) > 0 ? field.value : null}
                            onChange={(v) => field.onChange(v != null ? Number(v) : 0)}
                            placeholder="Select helper"
                            isDisabled={isLoadingDropdownData}
                          />
                        )}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>
                        Cash On Hand{' '}
                        <Text as="span" color="red.500">
                          *
                        </Text>
                      </FormLabel>
                        <Input
                          type="number"
                          {...register('cashOnHand', { valueAsNumber: true })}
                        />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>
                        Issued Cash Amount{' '}
                        <Text as="span" color="red.500">
                          *
                        </Text>
                      </FormLabel>
                      <Input
                        type="number"
                        {...register('issuedCashAmount', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>
                        Transaction Date{' '}
                        <Text as="span" color="red.500">
                          *
                        </Text>
                      </FormLabel>
                      <Input
                        type="date"
                        {...register('transactionDate')}
                      />
                    </Grid>
        
                  </VStack>
                )}
        
        
                {isEditMode === true && budgetManagementExpenseType === "Parts Expense" && (
                  <VStack align="stretch" spacing={3}>
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Shift</FormLabel>
                      <Select {...register('shift')}>
                        <option value="Day">Day</option>
                        <option value="Night">Night</option>
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Plate Number</FormLabel>
                      <Controller
                        name="plateNumber"
                        control={control}
                        render={({ field }) => (
                          <SearchableSelect
                            options={trucks.map((truck) => ({
                              id: truck.plate_number,
                              name: truck.plate_number,
                            }))}
                            optionsUrl="/trucks/truck-list"
                            optionsParams={{ is_active: 1 }}
                            optionIdKey="plate_number"
                            value={field.value ? field.value : null}
                            onChange={(v) => field.onChange(v != null ? String(v) : '')}
                            placeholder="Select plate number"
                            isDisabled={isLoadingDropdownData}
                          />
                        )}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Receipt Number</FormLabel>
                        <Input
                          type="text"
                          {...register('receiptNO')}
                        />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Quantity</FormLabel>
                      <Input
                        type="number"
                        {...register('quantity', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Article</FormLabel>
                      <Input
                        type="text"
                        {...register('article')}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Amount Per Item</FormLabel>
                      <Input
                        type="number"
                        {...register('amountPerItem', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Transaction Date</FormLabel>
                      <Input
                        type="date"
                        {...register('transactionDate')}
                      />
                    </Grid>
        
                  </VStack>
                )}
        
        
                {isEditMode === true && budgetManagementExpenseType === "Other Expense" && (
                  <VStack align="stretch" spacing={3}>
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Shift</FormLabel>
                      <Select {...register('shift')}>
                        <option value="Day">Day</option>
                        <option value="Night">Night</option>
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Remarks</FormLabel>
                      <Input
                        type="text"
                        {...register('remarks')}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Amount</FormLabel>
                      <Input
                        type="number"
                        {...register('amount', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Transaction Date</FormLabel>
                      <Input
                        type="date"
                        {...register('transactionDate')}
                      />
                    </Grid>
        
                  </VStack>
                )}
        
        
                {isEditMode === true && budgetManagementExpenseType === "Driver Cash Advance" && (
                  <VStack align="stretch" spacing={3}>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Amount</FormLabel>
                      <Input
                        type="number"
                        {...register('amount', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Transaction Date</FormLabel>
                      <Input
                        type="date"
                        {...register('transactionDate')}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Shift</FormLabel>
                      <Select {...register('shift')}>
                        <option value="Day">Day</option>
                        <option value="Night">Night</option>
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Driver</FormLabel>
                      <Controller
                        name="driverID"
                        control={control}
                        render={({ field }) => (
                          <SearchableSelect
                            options={drivers.map((d) => ({ id: d.id, name: d.full_name }))}
                            optionsUrl="/drivers"
                            optionsParams={{ is_active: 1 }}
                            value={field.value != null && Number(field.value) > 0 ? field.value : null}
                            onChange={(v) => field.onChange(v != null ? Number(v) : 0)}
                            placeholder="Select driver"
                            isDisabled={isLoadingDropdownData}
                          />
                        )}
                      />
                    </Grid>
                  </VStack>
                )}
        
        
                {isEditMode === true && budgetManagementExpenseType === "Helper Cash Advance" && (
                  <VStack align="stretch" spacing={3}>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Amount</FormLabel>
                      <Input
                        type="number"
                        {...register('amount', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Transaction Date</FormLabel>
                      <Input
                        type="date"
                        {...register('transactionDate')}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Shift</FormLabel>
                      <Select {...register('shift')}>
                        <option value="Day">Day</option>
                        <option value="Night">Night</option>
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Helper</FormLabel>
                      <Controller
                        name="helperID"
                        control={control}
                        render={({ field }) => (
                          <SearchableSelect
                            options={helpers.map((h) => ({ id: h.id, name: h.full_name }))}
                            optionsUrl="/helpers"
                            optionsParams={{ is_active: 1 }}
                            value={field.value != null && Number(field.value) > 0 ? field.value : null}
                            onChange={(v) => field.onChange(v != null ? Number(v) : 0)}
                            placeholder="Select helper"
                            isDisabled={isLoadingDropdownData}
                          />
                        )}
                      />
                    </Grid>
                  </VStack>
                )}


        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create New Budget Entry'}
          </Button>
        </HStack>
      </VStack>
    </Box>



  )
}